import { formatAdsFeedImportResult } from "@/components/ads-assistant/adsFeedImportResultText";
import { importAdsFeed } from "@/lib/adsAssistantClient";
import type { AdsFeedImportMode } from "@/components/ads-assistant/adsFeedImportSectionTypes";

export async function runAdsFeedImportAction(params: {
  mode: AdsFeedImportMode;
  location_id: string | null;
  pageId: string;
  overrideToken: string;
  canEditMetaToken: boolean;
  pasteJson: string;
  fetchLimit: number;
  daysBack: number;
  encryptionConfigured: boolean;
  metaConfigured: boolean;
  googleEncryptionConfigured: boolean;
  googleConfigured: boolean;
  salesGhlOnly: boolean;
  settingsLocationId: string | null;
  salesMustPickLocation: boolean;
}): Promise<{ ok: true; resultText: string; clearOverride?: boolean } | { ok: false; error: string }> {
  const override = params.canEditMetaToken ? params.overrideToken.trim() : "";

  if (params.mode === "facebook_graph_fetch") {
    if (!params.encryptionConfigured) {
      return { ok: false, error: "Server is not configured to store Meta tokens (META_FB_PAGE_TOKEN_ENCRYPTION_KEY)." };
    }
    if (!params.metaConfigured && !override) {
      return {
        ok: false,
        error:
          "No saved Meta page token for this org. Ask marketing, admin, owner, or dev to save one, or use an override if you have access.",
      };
    }
  }

  if (params.mode === "google_gbp_fetch") {
    if (!params.googleEncryptionConfigured) {
      return { ok: false, error: "Server is not configured to store Google credentials (GOOGLE_GBP_TOKEN_ENCRYPTION_KEY)." };
    }
    if (!params.googleConfigured) {
      return {
        ok: false,
        error:
          "No saved Google service account for this org. Ask marketing, admin, owner, or dev to save credentials in Advanced import.",
      };
    }
  }

  if (params.salesGhlOnly && !params.settingsLocationId) {
    return { ok: false, error: "Choose a location in Settings before syncing." };
  }

  if (params.salesMustPickLocation && !params.location_id) {
    return { ok: false, error: "Select your location before syncing." };
  }

  const r =
    params.mode === "ghl_social_planner"
      ? await importAdsFeed({
          mode: "ghl_social_planner",
          location_id: params.location_id,
          limit: params.fetchLimit,
          days_back: params.daysBack,
        })
      : params.mode === "facebook_graph_fetch"
        ? await importAdsFeed({
            mode: "facebook_graph_fetch",
            platform: "facebook",
            location_id: params.location_id,
            page_id: params.pageId.trim() || undefined,
            ...(override ? { page_access_token: override } : {}),
            limit: params.fetchLimit,
          })
        : params.mode === "facebook_graph_paste"
          ? await importAdsFeed({
              mode: "facebook_graph_paste",
              platform: "facebook",
              location_id: params.location_id,
              payload: params.pasteJson.trim() ? params.pasteJson : undefined,
            })
          : params.mode === "google_gbp_fetch"
            ? await importAdsFeed({
                mode: "google_gbp_fetch",
                location_id: params.location_id,
                limit: params.fetchLimit,
                days_back: params.daysBack,
              })
            : await importAdsFeed({
                mode: "google_local_posts_paste",
                location_id: params.location_id,
                payload: params.pasteJson.trim() ? params.pasteJson : undefined,
              });

  if (!r.ok) return { ok: false, error: r.error };

  return { ok: true, resultText: formatAdsFeedImportResult(r.data), clearOverride: !!override };
}
